import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { ArrowRight, Calendar, Database, FlaskConical, LayoutDashboard, Stethoscope, Users } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import Content from '../components/layout/Content';
import TestAnamneseDialog from '../components/testing/TestAnamneseDialog';
import TestPgBouncerDialog from '../components/testing/TestPgBouncerDialog';
import { useUserStore } from '~/stores/user-store';
import { UserRole } from '~/types/server-types/UserRole';

const DashboardCard = ({
    title,
    description,
    icon: Icon,
    onClick,
}: {
    title: string;
    description: string;
    icon: React.ElementType;
    onClick: () => void;
}) => {
    return (
        <Card className='cursor-pointer hover:shadow-lg transition-shadow' onClick={onClick}>
            <CardHeader className='flex flex-row items-center gap-4'>
                <div className='p-2 bg-primary/10 rounded-lg'>
                    <Icon className='h-6 w-6 text-primary' />
                </div>
                <div>
                    <CardTitle className='text-lg'>{title}</CardTitle>
                </div>
            </CardHeader>
            <CardContent>
                <CardDescription className='mb-4'>{description}</CardDescription>
                <div className='flex justify-end'>
                    <Button variant='ghost' size='sm' onClick={onClick}>
                        <ArrowRight className='h-4 w-4' />
                    </Button>
                </div>
            </CardContent>
        </Card>
    );
};

const Dashboard = () => {
    const navigate = useNavigate();
    const [testDialogOpen, setTestDialogOpen] = useState(false);
    const [pgBouncerDialogOpen, setPgBouncerDialogOpen] = useState(false);
    const { user } = useUserStore();

    const isAdmin = user?.role === UserRole.ADMIN;

    return (
        <Content>
            <div className='container mx-auto py-10'>
                <div className='flex items-center justify-between mb-8'>
                    <h1 className='text-3xl font-bold'>Dashboard</h1>
                    {isAdmin && (
                        <div className='flex gap-2'>
                            <Button
                                variant='outline'
                                size='sm'
                                onClick={() => setPgBouncerDialogOpen(true)}
                            >
                                <Database className='h-4 w-4 mr-2' />
                                Test PgBouncer
                            </Button>
                            <Button
                                variant='outline'
                                size='sm'
                                onClick={() => setTestDialogOpen(true)}
                            >
                                <FlaskConical className='h-4 w-4 mr-2' />
                                Test
                            </Button>
                        </div>
                    )}
                </div>
                <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-6'>
                    <DashboardCard
                        title='Termine'
                        description='Hier können Sie Termine verwalten. Sie können Termine erstellen, bearbeiten und löschen.'
                        icon={Calendar}
                        onClick={() => navigate('/appointments-calendar')}
                    />
                    <DashboardCard
                        title='Board'
                        description='Hier können Sie das Live-Board verwalten. Sie können Termine im Board anzeigen und verwalten.'
                        icon={LayoutDashboard}
                        onClick={() => navigate('/appointments-board')}
                    />
                    <DashboardCard
                        title='Kunden'
                        description='Hier können Sie Kunden verwalten. Sie können Kunden erstellen, bearbeiten und löschen.'
                        icon={Users}
                        onClick={() => navigate('/customers')}
                    />
                    <DashboardCard
                        title='Behandler'
                        description='Hier können Sie Behandler verwalten. Sie können Behandler erstellen, bearbeiten und löschen.'
                        icon={Stethoscope}
                        onClick={() => navigate('/practitioners')}
                    />
                </div>
            </div>

            <TestPgBouncerDialog
                open={pgBouncerDialogOpen}
                onClose={() => setPgBouncerDialogOpen(false)}
            />
            <TestAnamneseDialog
                open={testDialogOpen}
                onClose={() => setTestDialogOpen(false)}
            />
        </Content>
    );
};

export default Dashboard;
